import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography } from
'@mui/material';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {resolveTab} from "src/utils/tab.util";
import { DeduplicationTaskService } from 'crqlar-react-common/dist/services/profile/deduplicationTask.service';
import {
  DeduplicationStage,
  DeduplicationTask,
  DeduplicationTaskStatus } from
'crqlar-react-common/dist/models/profile/deduplicationTask.model';
import { useTranslation } from 'react-i18next';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockingPhaseCard from './BlockingPhaseCard';
import SimilarityPhaseCard from './SimilarityPhaseCard';
import ClusteringPhaseCard from './ClusteringPhaseCard';
import SavingPhaseCard from './SavingPhaseCard';
import MergingPhaseCard from './MergingPhaseCard';
import ClusterListTab from './ClusterListTab';
import { formatDuration } from './deduplicationUtils';
import { useReadOnly } from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import SettingsPageHeader from "../layout/SettingsPageHeader";

const DeduplicationDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const readOnlyUser = useReadOnly();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTab(searchParams.get('tab'), ['task', 'clusters'], 'task');
  const setActiveTab = (newTab: string) => {
    setSearchParams({ tab: newTab });
  };

  // Poll unmerge status to disable task creation while unmerging
  const { data: unmergeStatus } = useQuery({
    queryKey: ['unmergeStatusDashboard'],
    queryFn: DeduplicationTaskService.getUnmergeStatus,
    refetchInterval: 2000
  });

  const isUnmerging = unmergeStatus?.status === 'IN_PROGRESS' || unmergeStatus?.status === 'PENDING';

  // Fetch the latest deduplication task with 1s refetch
  const { data: task, isLoading, refetch } = useQuery({
    queryKey: ['deduplicationTask'],
    queryFn: DeduplicationTaskService.getLatest,
    refetchInterval: 1000 // Refetch every 1 second
  });

  // Create new task mutation
  const createTaskMutation = useMutation({
    mutationFn: DeduplicationTaskService.create,
    onSuccess: () => {
      refetch();
    }
  });

  const getActiveStep = (task?: DeduplicationTask | null): number => {
    if (!task || !task.stage) return -1;

    if (task.stage === DeduplicationStage.FAILED) return -1;

    // Map stages to step indices
    if (task.stage === DeduplicationStage.INITIALIZED ||
    task.stage === DeduplicationStage.BLOCKING_IN_PROGRESS) return 0;
    if (task.stage === DeduplicationStage.BLOCKING_COMPLETED ||
    task.stage === DeduplicationStage.SIMILARITY_SCORING_IN_PROGRESS) return 1;
    if (task.stage === DeduplicationStage.SIMILARITY_SCORING_COMPLETED ||
    task.stage === DeduplicationStage.CLUSTERING_IN_PROGRESS) return 2;
    if (task.stage === DeduplicationStage.CLUSTERING_COMPLETED ||
    task.stage === DeduplicationStage.SAVING_IN_PROGRESS) return 3;
    if (task.stage === DeduplicationStage.SAVING_COMPLETED ||
    task.stage === DeduplicationStage.MERGING_IN_PROGRESS) return 4;
    if (task.stage === DeduplicationStage.MERGING_COMPLETED ||
    task.stage === DeduplicationStage.COMPLETED) return 5;

    return -1;
  };

  const isStepActive = (stepIndex: number, task?: DeduplicationTask | null): boolean => {
    if (!task) return false;
    return getActiveStep(task) === stepIndex;
  };

  const getStatusChip = (task?: DeduplicationTask | null) => {
    if (!task) return null;

    if (task.status === DeduplicationTaskStatus.ERROR || task.stage === DeduplicationStage.FAILED) {
      return <Chip icon={<ErrorIcon />} label={t("Failed")} color="error" />;
    }
    if (task.status === DeduplicationTaskStatus.SUCCESS || task.stage === DeduplicationStage.COMPLETED) {
      return <Chip icon={<CheckCircleIcon />} label={t("Completed")} color="success" />;
    }
    if (task.status === DeduplicationTaskStatus.IN_PROGRESS) {
      return <Chip icon={<HourglassEmptyIcon />} label={t("In Progress")} color="primary" />;
    }
    return <Chip label={t("Pending")} color="default" />;
  };

  const isTaskRunning = task?.status === DeduplicationTaskStatus.IN_PROGRESS;

  const headerSection = (
    <SettingsPageHeader
      title={t('Profile Deduplication')}
      back={() => navigate('/system')}
      loading={isLoading || createTaskMutation.isLoading}
      actions={(
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon/>}
          onClick={() => createTaskMutation.mutate()}
          disabled={isLoading || createTaskMutation.isLoading || isTaskRunning || isUnmerging || readOnlyUser}
        >
          {t('Start New Task')}
        </Button>
      )}
    />
  );


  if (isLoading) {
    return (
      <Box sx={{ width: '100%', pb: 8 }}>
                {headerSection}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                </Box>
            </Box>);

  }

  return (
    <Box sx={{ width: '100%', pb: 8 }}>
            {headerSection}
            <Stack sx={{ width: '100%', maxWidth: '900px', mx: 'auto', mt: 3, gap: 2 }}>
                <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            mb: 4,
            borderBottom: 1,
            borderColor: 'divider'
          }}>

                    <Tab label={t('Task')} value={'task'} />
                    <Tab label={t('Clusters')} value={'clusters'} />
                </Tabs>

                {activeTab === 'task' &&
        <>
                        {task &&
          <Paper variant="outlined" sx={{ p: 4, mb: 5 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="h6" color="text.secondary" gutterBottom>
                                            {t("Task Status")}
                                        </Typography>
                                        {getStatusChip(task)}
                                    </Box>
                                    {task.totalDurationMs &&
              <Box>
                                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                                {t("Total Duration")}
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                                {formatDuration(task.totalDurationMs)}
                                            </Typography>
                                        </Box>
              }
                                </Stack>
                                {task.errorMessage &&
            <Box sx={{ mt: 3, p: 3, bgcolor: 'error.light', borderRadius: 2 }}>
                                        <Typography variant="h6" color="error.dark">
                                            {t("Error:")} {task.errorMessage}
                                        </Typography>
                                    </Box>
            }
                            </Paper>
          }

                        {!task &&
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    {t("No deduplication task found")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("Start a new task to begin the deduplication process")}
                                </Typography>
                            </Paper>
          }

                        {task &&
          <Box sx={{ width: '100%' }}>
                                {/* Phase 1: Blocking */}
                                {task.blockingStats &&
            <Box sx={{ mb: 5 }}>
                                        <BlockingPhaseCard task={task} />
                                    </Box>
            }
                                {isStepActive(0, task) && !task.blockingStats &&
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="h6">{t("Processing blocking phase...")}</Typography>
                                    </Box>
            }

                                {/* Phase 2: Similarity */}
                                {task.similarityStats &&
            <Box sx={{ mb: 5 }}>
                                        <SimilarityPhaseCard task={task} />
                                    </Box>
            }
                                {isStepActive(1, task) && !task.similarityStats &&
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="h6">{t("Scoring pairs...")}</Typography>
                                    </Box>
            }

                                {/* Phase 3: Clustering */}
                                {task.clusteringStats &&
            <Box sx={{ mb: 5 }}>
                                        <ClusteringPhaseCard task={task} />
                                    </Box>
            }
                                {isStepActive(2, task) && !task.clusteringStats &&
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="h6">{t("Building clusters...")}</Typography>
                                    </Box>
            }

                                {/* Phase 4: Saving */}
                                {task.savingStats &&
            <Box sx={{ mb: 5 }}>
                                        <SavingPhaseCard task={task} />
                                    </Box>
            }
                                {isStepActive(3, task) && !task.savingStats &&
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="h6">{t("Saving clusters...")}</Typography>
                                    </Box>
            }

                                {/* Phase 5: Merging */}
                                {task.mergingStats &&
            <Box sx={{ mb: 5 }}>
                                        <MergingPhaseCard task={task} />
                                    </Box>
            }
                                {isStepActive(4, task) && !task.mergingStats &&
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="h6">{t("Merging profiles...")}</Typography>
                                    </Box>
            }

                                {/* Phase 6: Completed */}
                                {task.stage === DeduplicationStage.COMPLETED &&
            <Card variant="outlined" sx={{ p: 3 }}>
                                        <CardContent>
                                            <Typography variant="h5" color="success.main" gutterBottom>
                                                {t("Deduplication Complete")}
                                            </Typography>
                                            <Typography variant="h6" color="text.secondary">
                                                {t("The deduplication process has been completed successfully.")}
                                            </Typography>
                                        </CardContent>
                                    </Card>
            }
                            </Box>
          }
                    </>
        }

                {activeTab === 'clusters' &&
        <ClusterListTab isTaskRunning={isTaskRunning} />
        }
            </Stack>
        </Box>);

};

export default DeduplicationDashboard;
